import {
  canvasIdAtom,
  isViewOnlyModeAtom,
  takeSnapshotAtom,
  createNotificationAtom,
  handleImageUploadStartCommand,
} from "@zapier/canvas-state";
import { Text } from "@zapier/design-system";
import {
  ContentType,
  RemirrorJSON,
  WysiwygEditor,
} from "@zapier/rich-text-editor";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";

import useUpdateNode from "../../hooks/useUpdateNode";

export function Notes({
  notesJson,
  nodeId,
}: {
  nodeId: string;
  notesJson: RemirrorJSON | undefined;
}) {
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const updateNodeAndSave = useUpdateNode();
  const canvasId = useAtomValue(canvasIdAtom);

  const handleNotesOnBlur = useCallback(
    (data: ContentType) => {
      takeSnapshot();
      updateNodeAndSave({
        id: nodeId,
        data: {
          notesJson: data,
        },
      });
    },
    [updateNodeAndSave, nodeId, takeSnapshot],
  );

  const handleImageUploadStart = useAtomCallback(
    useCallback((_get, set) => {
      set(
        handleImageUploadStartCommand,
        "Uploading image... Please keep the sidebar open.",
      );
    }, []),
  );

  const handleImageUploadError = useAtomCallback(
    useCallback((_get, set, message?: string) => {
      set(createNotificationAtom, {
        type: "error",
        message:
          message ||
          "We ran into an issue uploading your image. Please try again.",
      });
    }, []),
  );

  return (
    <>
      <Text type="SmallPrint2">Notes</Text>
      <WysiwygEditor
        // Even though this component is not rendered in a list, it still uses the `key` prop
        // to force a re-render when the node changes. If you remove key, the editor will not
        // update to show the new node's content. when you select a new node without closing the sidebar
        key={nodeId}
        nodeId={nodeId}
        canvasId={canvasId}
        initialContent={notesJson}
        onBlur={handleNotesOnBlur}
        editable={!isViewOnlyMode}
        placeholder={isViewOnlyMode ? undefined : "Add notes..."}
        classNames={["wysiwyg-editor-sidebar"]}
        theme={{
          radius: {
            border: "3px",
          },
          space: {
            3: "5px",
          },
        }}
        onImageUploadError={handleImageUploadError}
        onImageUploadStart={handleImageUploadStart}
      />
    </>
  );
}
